import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { toast } from "sonner";
import { pageItems } from "../lib/pageItems";
import { MessageCircle, Send } from "lucide-react";

export default function TripMessagesPanel({ bookingId, compact = false }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bookings/${bookingId}/messages`, { withCredentials: true });
      setMessages(pageItems(r.data));
    } catch {
      if (!compact) toast.error("Could not load messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [bookingId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/bookings/${bookingId}/messages`,
        { body: text },
        { withCredentials: true },
      );
      setMessages((m) => [...m, r.data]);
      setBody("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (!bookingId) return null;

  return (
    <div className={compact ? "mt-3" : "zc-card p-4"} data-testid={`trip-messages-${bookingId}`}>
      <div className="flex items-center gap-2 mb-3">
        <MessageCircle size={14} className="text-[var(--zc-accent)]" />
        <div className="text-sm font-semibold">Trip messages</div>
      </div>
      <div
        className={
          "rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg-2)] overflow-y-auto space-y-2 p-3 " +
          (compact ? "max-h-40" : "max-h-56")
        }
      >
        {loading && <div className="text-xs text-[var(--zc-text-3)]">Loading…</div>}
        {!loading && messages.length === 0 && (
          <div className="text-xs text-[var(--zc-text-3)]">No messages yet — coordinate pick-up or return here.</div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user?.user_id;
          return (
            <div
              key={m.message_id}
              className={"text-xs rounded-lg px-3 py-2 max-w-[90%] " + (mine ? "ml-auto bg-[var(--zc-accent-soft)]" : "bg-white border border-[var(--zc-border)]")}
            >
              <div className="font-semibold text-[var(--zc-text-2)]">{m.sender_name || "Guest"}</div>
              <div className="mt-0.5 whitespace-pre-wrap break-words">{m.body}</div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 mt-3">
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Message your host or renter…"
          className="zc-input h-10 px-3 flex-1 text-sm min-w-0"
          data-testid={`trip-msg-input-${bookingId}`}
        />
        <button
          type="submit"
          disabled={sending || !body.trim()}
          className="px-3 h-10 zc-btn-primary shrink-0"
          data-testid={`trip-msg-send-${bookingId}`}
        >
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}
